import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { authAPI, APIError } from '../services/api';
import { logger } from '../lib/logger';

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'user' | 'viewer';
  twoFactorEnabled: boolean;
  avatar?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  users: User[];
  apiKey: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithApiKey: (apiKey: string) => Promise<void>;
  logout: () => void;
  register: (name: string, email: string, password: string) => Promise<void>;
  addUser: (user: Omit<User, 'id'>) => void;
  deleteUser: (id: string) => void;
  enableTwoFactor: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Initialize from localStorage synchronously to avoid flash.
  // Guard with try/catch: Vivaldi and other Chromium-based browsers can corrupt
  // localStorage entries when the browser is force-closed mid-write. A truncated
  // JSON value causes JSON.parse to throw a SyntaxError synchronously inside the
  // useState initializer — before any React error boundary is in scope — which
  // crashes the entire app on every subsequent load. Clear and fall back to null.
  const [user, setUser] = useState<User | null>(() => {
    try {
      const storedUser = localStorage.getItem('crawl_ai_user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch {
      localStorage.removeItem('crawl_ai_user');
      return null;
    }
  });
  const [users, setUsers] = useState<User[]>([]);
  const [apiKey, setApiKey] = useState<string | null>(() => localStorage.getItem('crawl_ai_api_key'));
  const [isLoading, setIsLoading] = useState(() => {
    // Only loading if we have an apiKey but no user yet
    const hasKey = !!localStorage.getItem('crawl_ai_api_key');
    const hasUser = !!localStorage.getItem('crawl_ai_user');
    return hasKey && !hasUser;
  });

  // Validate user profile on mount (only if we have apiKey but need to verify)
  useEffect(() => {
    const storedApiKey = localStorage.getItem('crawl_ai_api_key');
    if (storedApiKey && !user) {
      setIsLoading(true);
      authAPI.getProfile(storedApiKey)
        .then((profile) => {
          setUser({
            id: profile.id.toString(),
            name: profile.username,
            email: profile.email,
            role: profile.role,
            twoFactorEnabled: false,
            avatar: undefined
          });
        })
        .catch(() => {
          // Invalid token, clear it
          localStorage.removeItem('crawl_ai_api_key');
          localStorage.removeItem('crawl_ai_user');
          setApiKey(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, []);

  // Save to local storage on change
  useEffect(() => {
    if (user) {
      localStorage.setItem('crawl_ai_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('crawl_ai_user');
    }
  }, [user]);

  const login = useCallback(async (username: string, password: string) => {
    try {
      const response = await authAPI.login(username, password);

      // Store API key and JWT token
      localStorage.setItem('crawl_ai_api_key', response.api_key);
      localStorage.setItem('crawl_ai_jwt_token', response.access_token);
      setApiKey(response.api_key);

      // Set user from response
      setUser({
        id: response.user.id.toString(),
        name: response.user.username,
        email: response.user.email,
        role: response.user.role, // Use role from backend (admin, user, viewer)
        twoFactorEnabled: false,
        avatar: undefined
      });
    } catch (error) {
      if (error instanceof APIError) {
        throw new Error(error.message);
      }
      throw new Error('Login failed. Please try again.');
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setApiKey(null);
    localStorage.removeItem('crawl_ai_api_key');
    localStorage.removeItem('crawl_ai_jwt_token');
    localStorage.removeItem('crawl_ai_user');
  }, []);

  const loginWithApiKey = useCallback(async (key: string) => {
    try {
      const profile = await authAPI.getProfile(key);

      // Store API key
      localStorage.setItem('crawl_ai_api_key', key);
      setApiKey(key);

      // Set user from profile response
      setUser({
        id: profile.id.toString(),
        name: profile.username,
        email: profile.email,
        role: profile.role,
        twoFactorEnabled: false,
        avatar: undefined
      });
    } catch (error) {
      if (error instanceof APIError) {
        throw new Error(error.message);
      }
      throw new Error('Invalid API key. Please check and try again.');
    }
  }, []);

  const register = useCallback(async (name: string, email: string, password: string) => {
    try {
      const response = await authAPI.register(name, email, password);

      // Store API key and JWT token from registration
      localStorage.setItem('crawl_ai_api_key', response.api_key);
      localStorage.setItem('crawl_ai_jwt_token', response.access_token);
      setApiKey(response.api_key);

      // Set user from response (auto-login after registration)
      setUser({
        id: response.user.id.toString(),
        name: response.user.username,
        email: response.user.email,
        role: response.user.role,
        twoFactorEnabled: false,
        avatar: undefined
      });
    } catch (error) {
      if (error instanceof APIError) {
        throw new Error(error.message);
      }
      throw new Error('Registration failed. Please try again.');
    }
  }, []);

  const addUser = useCallback((userData: Omit<User, 'id'>) => {
    // This is for admin user management - keep mock for now
    const newUser: User = {
      ...userData,
      id: Math.random().toString(36).substr(2, 9)
    };
    setUsers(prev => [...prev, newUser]);
  }, []);

  const deleteUser = useCallback((id: string) => {
    // This is for admin user management - keep mock for now
    setUsers(prev => prev.filter(u => u.id !== id));
  }, []);

  const enableTwoFactor = useCallback(() => {
    // This is for 2FA - implement later
    setUser(prev => prev ? { ...prev, twoFactorEnabled: true } : null);
    setUsers(prev => prev.map(u => u.id === user?.id ? { ...u, twoFactorEnabled: true } : u));
  }, [user?.id]);

  const refreshUser = useCallback(async () => {
    if (!apiKey) return;

    try {
      const profile = await authAPI.getProfile(apiKey);
      setUser({
        id: profile.id.toString(),
        name: profile.username,
        email: profile.email,
        role: profile.role,
        twoFactorEnabled: false,
        avatar: undefined
      });
    } catch (error) {
      // If refresh fails, don't log out - just leave current state
      logger.error('Failed to refresh user profile:', error);
    }
  }, [apiKey]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    isAuthenticated: !!user,
    isLoading,
    users,
    apiKey,
    login,
    loginWithApiKey,
    logout,
    register,
    addUser,
    deleteUser,
    enableTwoFactor,
    refreshUser
  }), [user, isLoading, users, apiKey, login, loginWithApiKey, logout, register, addUser, deleteUser, enableTwoFactor, refreshUser]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
